import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import ScrollToTop from './components/ScrollToTop';
import { applyTheme, readTheme } from './components/ThemeToggle';
import { toast } from 'sonner';
import { BarChart3 } from 'lucide-react';

// Components
import AuthForm from './components/BrandAuthForm';
import { signupClickId } from './lib/signupAttribution';
import SsoCallback from './components/SsoCallback';
import VerifyEmail from './components/VerifyEmail';
import Dashboard from './components/Dashboard';
import Campaigns from './components/Campaigns';
import CampaignDetail from './components/CampaignDetail';
import Streams from './components/Streams';
import AIOptimizer from './components/AIOptimizer';
import TrafficSimulation from './components/TrafficSimulation';
import Settings from './components/Settings';
import TrafficManagement from './components/TrafficManagement';
import TrafficSources from './components/TrafficSources';
import AntiFraud from './components/AntiFraud';
import ABTesting from './components/ABTesting';
import Analytics from './components/Analytics';
import ClicksLog from './components/ClicksLog';
import SubIdReports from './components/SubIdReports';
import CampaignComparison from './components/CampaignComparison';
import NotFound from './components/NotFound';
import Automation from './components/Automation';
import Integrations from './components/Integrations';
import LandingPages from './components/LandingPages';
import WhitePages from './components/WhitePages';
import PreLandings from './components/PreLandings';
import AdAccountConnections from './components/AdAccountConnections';
import AdminAudit from './components/AdminAudit';
import AdminCapiDlq from './components/AdminCapiDlq';
import AdminErrors from './components/AdminErrors';
import AdminHealth from './components/AdminHealth';
import PlatformAdmin from './components/PlatformAdmin';
import SystemStatus from './components/SystemStatus';
import S2SLogs from './components/S2SLogs';
import TlsFingerprints from './components/TlsFingerprints';
import SecuritySettings from './components/SecuritySettings';
import NotificationPreferences from './components/NotificationPreferences';
import Offers from './components/Offers';
import AffiliateNetworks from './components/AffiliateNetworks';
import Reports from './components/Reports';
import Leads from './components/Leads';
import Domains from './components/Domains';
import ConsentBanner from './components/ConsentBanner';
import Marketplace from './components/Marketplace';
import ConversionForwarders from './components/ConversionForwarders';
import ConversionTypes from './components/ConversionTypes';
import Trends from './components/Trends';
import CustomMetrics from './components/CustomMetrics';
import BotLists from './components/BotLists';
import SavedReports from './components/SavedReports';
import ScheduledReports from './components/ScheduledReports';
import CostEntries from './components/CostEntries';
import GdprDsar from './components/GdprDsar';
import ApiKeys from './components/ApiKeys';
import Workspace from './components/Workspace';
import ArchivePage from './components/ArchivePage';
import MemberAccess from './components/MemberAccess';
import ClickTraces from './components/ClickTraces';
import BotModelPanel from './components/BotModelPanel';
import AcceptInvite from './components/AcceptInvite';
import Branding from './components/Branding';
import ErrorBoundary from './components/ErrorBoundary';
import { installErrorReporter } from './lib/errorReporter';
import { installNetworkRetry } from './lib/networkRetry';
import { installApiErrorDetailInterceptor } from './lib/apiErrorDetail';
import Billing from './components/Billing';
import BillingGate from './components/BillingGate';
import LicenceGate from './components/LicenceGate';
import SelfHosted from './components/SelfHosted';
import CloudOnly from './components/CloudOnly';
import SelfHostedGuide from './components/SelfHostedGuide';
import Documentation from './components/Documentation';
import PublicDashboard from './components/PublicDashboard';
import Navbar from './components/Navbar';
import XpnsrTopbar from './components/XpnsrTopbar';
import TrialBanner from './components/TrialBanner';
import LocaleSync from './components/LocaleSync';
import XpnsrCommandPalette from './components/XpnsrCommandPalette';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import OfflineBanner from './components/OfflineBanner';

import { LocaleProvider, useT } from './i18n';
import { BrandingProvider } from './branding';
import { BACKEND_URL as RUNTIME_BACKEND_URL } from './runtimeConfig';
import './App.css';

// Theme is owned by ThemeToggle — it reads the persisted choice from
// localStorage on mount, falls back to the OS preference, and writes the
// `dark` class on <html>. We DO NOT force `dark` here anymore: doing so
// stomped the toggle on every render so users were stuck on dark mode no
// matter what they clicked. To preempt the FOUC from the toggle's first
// effect, set the class synchronously from the same source of truth the
// component will use.
if (typeof document !== 'undefined') {
  // Same source of truth as ThemeToggle (stored choice, else the brand default).
  applyTheme(readTheme());
}

// Resolve the backend base URL strictly from REACT_APP_BACKEND_URL. We used
// to fall back to the production Railway host when the env var was unset,
// which meant a developer running `yarn start` without a `.env.local` would
// silently drive PRODUCTION traffic from their dev tab — easy to mistake a
// staging UI for the local backend and mutate real customer data. Now we
// fail loud: surface the misconfiguration in the console (and in the UI on
// first render) so it's caught before any API call goes out.
const RESOLVED_BACKEND_URL = RUNTIME_BACKEND_URL;
if (!RESOLVED_BACKEND_URL) {
  const msg =
    'REACT_APP_BACKEND_URL is not set. Configure it in frontend/.env (e.g. ' +
    'http://localhost:8002) before starting the dev server. The app will not ' +
    'attempt any API calls until this is fixed.';
  // eslint-disable-next-line no-console
  console.error(msg);
  if (typeof window !== 'undefined') {
    // setTimeout so the alert fires after React mounts — otherwise it can
    // race with the initial render and feel like a hang.
    setTimeout(() => {
      try { window.alert(msg); } catch (_) {}
    }, 0);
  }
}
const BACKEND_URL = RESOLVED_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Graceful session-expiry handling. When a stored JWT expires mid-session,
// every subsequent API call starts returning 401 — without a global handler
// the app just surfaces a cascade of error toasts and half-loaded pages until
// the user manually reloads. This interceptor clears the dead token and
// bounces to the login screen exactly once, so an expired session degrades
// cleanly to "please sign in again" instead of looking broken.
//
// Auth-flow endpoints are excluded so their own 401s (wrong password, bad 2FA
// code) keep showing inline error toasts instead of triggering a redirect, and
// we only act when a token was actually present (a 401 with no stored token is
// a normal pre-login response, not an expiry).
let _sessionExpiryInterceptorInstalled = false;
function installSessionExpiryInterceptor() {
  if (_sessionExpiryInterceptorInstalled) return;
  _sessionExpiryInterceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = String(error?.config?.url || '');
      const isAuthFlow = /\/auth\/(login|register|2fa\/login|sso\/exchange)/.test(url);
      const hadToken = !!localStorage.getItem('token');
      if (status === 401 && hadToken && !isAuthFlow) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        if (!window.location.pathname.startsWith('/auth')) {
          window.location.assign('/auth?expired=1');
        }
      }
      return Promise.reject(error);
    }
  );
}
installSessionExpiryInterceptor();

// FastAPI 422 responses carry a list in `detail`; turn it into text before any
// component renders it (React error #31). See src/lib/apiErrorDetail.js.
installApiErrorDetailInterceptor(axios);

// Attach the bearer per request from the stored token. The defaults header is
// only set from a useEffect, which runs after the first authenticated render;
// components that fetch on mount (the notification bell) were firing one
// request without it right after login and logging a 403.
let _authHeaderInterceptorInstalled = false;
function installAuthHeaderInterceptor() {
  if (_authHeaderInterceptorInstalled) return;
  _authHeaderInterceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    const url = String(config?.url || '');
    const token = localStorage.getItem('token');
    const isOurApi = url.startsWith(API) || url.startsWith('/api');
    if (token && isOurApi && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}
installAuthHeaderInterceptor();

// Retry idempotent API calls that got no response at all (a network blip, or the
// backend restarting during a deploy) before anything reports them.
installNetworkRetry(axios, { apiBase: API });

// In-app error reports: uncaught errors and 5xx / network failures of our API go
// to /api/public/client-errors (src/lib/errorReporter.js). Installed after the
// interceptors above, so it sees the error the app sees.
installErrorReporter({ apiBase: API, axiosInstance: axios });

// Auth Context
const AuthContext = React.createContext();
const MobileSidebarContext = React.createContext();

// Auth Provider
function AuthProvider({ children }) {
  const { t } = useT();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Setup axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check token validity on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          // Token is invalid
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user, requires_2fa, challenge_token } = response.data;

      // Wave 12 — when the account has 2FA on, the backend replies with
      // a challenge token instead of a real access token. Surface it so
      // the LoginPage can render the second-factor prompt.
      if (requires_2fa) {
        return { requires_2fa: true, challenge_token, user };
      }

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);

      toast.success(t('Signed in successfully!'));
      return { ok: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Unconfirmed signup — AuthForm shows the "check your inbox" panel
      // with a resend button instead of a dead-end error toast.
      if (error.response?.status === 403 && detail === 'Email not verified') {
        return { ok: false, email_not_verified: true, email };
      }
      // Pass the real reason back so the form can show it. Reporting every
      // failure as "wrong password" hid rate limiting, blocked accounts and
      // network errors behind a message that sent people hunting for a typo.
      const reason =
        detail ||
        (error.response
          ? t('Sign in failed') + ' (' + error.response.status + ')'
          : t('Cannot reach the server. Check your connection and try again.'));
      toast.error(reason);
      return { ok: false, detail: reason };
    }
  };

  const verify2fa = async (challenge_token, code) => {
    try {
      const response = await axios.post(`${API}/auth/2fa/login`, {
        challenge_token,
        code,
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      toast.success(t('Signed in successfully!'));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Invalid 2FA code'));
      return false;
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const clickId = signupClickId();
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
        ...(clickId ? { signup_click_id: clickId } : {})
      });
      const { access_token, user, verification_required } = response.data;

      // Signup email verification is active: the account exists but gets
      // no token until the emailed link is clicked. AuthForm switches to
      // its "check your inbox" panel on this flag.
      if (verification_required) {
        return { verification_required: true, email };
      }

      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);

      toast.success(t('Registration successful!'));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Registration failed'));
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success(t('Signed out'));
  };

  // Wave 15 — used by the SSO callback page after a successful
  // ``POST /api/auth/sso/exchange``. Mirrors the persistence side of
  // ``login`` but skips the password POST since the access token is
  // already issued.
  const applySsoSession = (accessToken, signedInUser) => {
    if (!accessToken) return;
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    if (signedInUser) {
      setUser(signedInUser);
    }
  };

  const value = {
    user,
    login,
    verify2fa,
    register,
    logout,
    applySsoSession,
    isAuthenticated: !!user
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="relative">
          <div className="w-20 h-20 bg-primary rounded-xl flex items-center justify-center animate-pulse">
            <BarChart3 className="w-10 h-10 text-primary-foreground" />
          </div>
          <div className="absolute -inset-6 bg-primary/25 rounded-[2rem] blur-2xl animate-pulse"></div>
        </div>
        <div className="mt-10 text-center">
          <div className="font-mono text-[0.7rem] uppercase tracking-[0.25em] text-primary mb-3">
            // xpnsr.tech
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-foreground mb-2">
            {t('Loading workspace')}
          </h2>
          <p className="text-muted-foreground text-sm">{t('One moment…')}</p>
          <div className="mt-6 w-64 h-[3px] bg-surface-2 rounded-full overflow-hidden mx-auto">
            <div className="h-full w-full animate-shimmer" />
          </div>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  // LicenceGate: self-hosted installs need a valid lease (no-op on our cloud).
  // BillingGate: no workspace without a subscription once billing is on.
  return isAuthenticated ? (
    <LicenceGate>
      <BillingGate>{children}</BillingGate>
    </LicenceGate>
  ) : (
    <Navigate to="/auth" />
  );
}

// Platform-admin Route Component — operator-only pages (the cross-tenant
// back-office). Hiding the nav entry is cosmetic; this guard also stops
// direct-URL access for clients. The API itself is gated server-side via
// require_platform_admin, so this is UX, not the security boundary.
function PlatformAdminRoute({ children }) {
  const { user } = useAuth();
  return user?.is_admin ? children : <Navigate to="/dashboard" />;
}

// Public Route Component (redirect to dashboard if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
}

// /login, /register and friends (linked from the marketing sites) land on
// /auth, which sends signed-in users straight to the dashboard.
function AuthAlias({ signup = false }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (signup && !params.has('signup')) params.set('signup', '1');
  const query = params.toString();
  return <Navigate to={'/auth' + (query ? '?' + query : '')} replace />;
}

// Mobile sidebar state
function MobileSidebarProvider({ children }) {
  const [open, setOpen] = React.useState(false);
  const toggle = React.useCallback(() => setOpen(v => !v), []);
  const close = React.useCallback(() => setOpen(false), []);
  const value = React.useMemo(() => ({ open, toggle, close }), [open, toggle, close]);
  return <MobileSidebarContext.Provider value={value}>{children}</MobileSidebarContext.Provider>;
}

const useMobileSidebar = () => React.useContext(MobileSidebarContext);

// Layout Component
function Layout({ children }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <MobileSidebarProvider>
      <div className="xp-app">
        <Navbar />
        <main className="xp-main">
          <XpnsrTopbar />
          <TrialBanner />
          {children}
        </main>
        <XpnsrCommandPalette />
        <KeyboardShortcuts />
        <LocaleSync />
      </div>
    </MobileSidebarProvider>
  );
}

// Hook for reveal animations
function useRevealAnimations() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    reveals.forEach(el => el.classList.add('visible'));
  });
}

function App() {
  useRevealAnimations();

  return (
    <ErrorBoundary>
    <LocaleProvider>
      <BrandingProvider apiBase={API}>
      <AuthProvider>
        <Router>
        <Layout>
          <Routes>
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <AuthForm />
                </PublicRoute>
              }
            />
            <Route path="/login" element={<AuthAlias />} />
            <Route path="/signin" element={<AuthAlias />} />
            <Route path="/sign-in" element={<AuthAlias />} />
            <Route path="/register" element={<AuthAlias signup />} />
            <Route path="/signup" element={<AuthAlias signup />} />
            <Route path="/sign-up" element={<AuthAlias signup />} />
            <Route
              path="/auth/sso/complete"
              element={<SsoCallback />}
            />
            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns"
              element={
                <ProtectedRoute>
                  <Campaigns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:id"
              element={
                <ProtectedRoute>
                  <CampaignDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:campaignId/streams"
              element={
                <ProtectedRoute>
                  <Streams />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:campaignId/ai-optimize"
              element={
                <ProtectedRoute>
                  <AIOptimizer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/:campaignId/simulate"
              element={
                <ProtectedRoute>
                  <TrafficSimulation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conversion-types"
              element={
                <ProtectedRoute>
                  <ConversionTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/trends"
              element={
                <ProtectedRoute>
                  <Trends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/custom-metrics"
              element={
                <ProtectedRoute>
                  <CustomMetrics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/bot-lists"
              element={
                <ProtectedRoute>
                  <BotLists />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/saved"
              element={
                <ProtectedRoute>
                  <SavedReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/scheduled"
              element={
                <ProtectedRoute>
                  <ScheduledReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cost-entries"
              element={
                <ProtectedRoute>
                  <CostEntries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/gdpr"
              element={
                <ProtectedRoute>
                  <GdprDsar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/traffic"
              element={
                <ProtectedRoute>
                  <TrafficManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/traffic-sources"
              element={
                <ProtectedRoute>
                  <TrafficSources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/anti-fraud"
              element={
                <ProtectedRoute>
                  <AntiFraud />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ab-testing"
              element={
                <ProtectedRoute>
                  <ABTesting />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clicks/log"
              element={
                <ProtectedRoute>
                  <ClicksLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sub-ids"
              element={
                <ProtectedRoute>
                  <SubIdReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns/compare"
              element={
                <ProtectedRoute>
                  <CampaignComparison />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation"
              element={
                <ProtectedRoute>
                  <Automation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <Integrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landing-pages"
              element={
                <ProtectedRoute>
                  <LandingPages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/white-pages"
              element={
                <ProtectedRoute>
                  <WhitePages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pre-landings"
              element={
                <ProtectedRoute>
                  <PreLandings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ad-account-connections"
              element={
                <ProtectedRoute>
                  <AdAccountConnections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/security"
              element={
                <ProtectedRoute>
                  <SecuritySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications/preferences"
              element={
                <ProtectedRoute>
                  <NotificationPreferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <ProtectedRoute>
                  <AdminAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/platform"
              element={
                <ProtectedRoute>
                  <PlatformAdminRoute>
                    <PlatformAdmin />
                  </PlatformAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/capi-dlq"
              element={
                <ProtectedRoute>
                  <AdminCapiDlq />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/errors"
              element={
                <ProtectedRoute>
                  <PlatformAdminRoute>
                    <AdminErrors />
                  </PlatformAdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health"
              element={
                <ProtectedRoute>
                  <AdminHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/system"
              element={
                <ProtectedRoute>
                  <SystemStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace/access"
              element={
                <ProtectedRoute>
                  <MemberAccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/diagnostics/click-traces"
              element={
                <ProtectedRoute>
                  <ClickTraces />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bot-model"
              element={
                <ProtectedRoute>
                  <BotModelPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logs/s2s"
              element={
                <ProtectedRoute>
                  <S2SLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/anti-fraud/tls"
              element={
                <ProtectedRoute>
                  <TlsFingerprints />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute>
                  <Offers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/affiliate-networks"
              element={
                <ProtectedRoute>
                  <AffiliateNetworks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/domains"
              element={
                <ProtectedRoute>
                  <Domains />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conversion-api"
              element={
                <ProtectedRoute>
                  <ConversionForwarders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <ProtectedRoute>
                  <ApiKeys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace"
              element={
                <ProtectedRoute>
                  <Workspace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/archive"
              element={
                <ProtectedRoute>
                  <ArchivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace/accept/:token"
              element={<AcceptInvite />}
            />
            <Route
              path="/workspace/branding"
              element={
                <ProtectedRoute>
                  <Branding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <CloudOnly>
                    <Billing />
                  </CloudOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/self-hosted"
              element={
                <ProtectedRoute>
                  <CloudOnly>
                    <SelfHosted />
                  </CloudOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/self-hosted/guide"
              element={
                <ProtectedRoute>
                  <CloudOnly>
                    <SelfHostedGuide />
                  </CloudOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/consent-banner"
              element={
                <ProtectedRoute>
                  <ConsentBanner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <Marketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/docs"
              element={
                <ProtectedRoute>
                  <Documentation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/public/dashboard/:token"
              element={<PublicDashboard />}
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors theme="system" />
          <ScrollToTop />
          <OfflineBanner />
        </Layout>
        </Router>
      </AuthProvider>
      </BrandingProvider>
    </LocaleProvider>
    </ErrorBoundary>
  );
}

export default App;
export { useAuth, useMobileSidebar, API };
